import type { ScoreRange } from '../../lib/types';
import { getWeatherScore } from '../../utils/score';

interface radius {
  tl?: number;
  tr?: number;
  br?: number;
  bl?: number;
}

interface rgb {
  r: number;
  g: number;
  b: number;
}

interface paletteDefinition {
  id: PaletteModeId;
  label: string;
  description: string;
  swatch: string;
  stops: string[];
  chartPositive: string;
  chartNegative: string;
  chartTrack: string;
}

export type PaletteModeId = 'contrast' | 'cividis' | 'viridis';

const c = 1 - 0.55191502449; // This constant is based off of this article by Spencer Mortensen http://spencermortensen.com/articles/bezier-circle/

const neutralFill = { r: 213, g: 219, b: 225 };

export const defaultPaletteMode: PaletteModeId = 'viridis';

export const paletteModes: paletteDefinition[] = [
  {
    id: 'cividis',
    label: 'Cividis',
    description: 'Safest blue-to-gold option for color-vision deficiency.',
    swatch: 'linear-gradient(90deg, #00204c 0%, #2f4f73 28%, #626e7d 55%, #9f9447 78%, #fee838 100%)',
    stops: ['#00204c', '#2f4f73', '#626e7d', '#9f9447', '#fee838'],
    chartPositive: '#355f8d',
    chartNegative: '#9f7e2e',
    chartTrack: '#d5dde5'
  },
  {
    id: 'viridis',
    label: 'Viridis',
    description: 'Best general-purpose sequential palette for ordered scores.',
    swatch: 'linear-gradient(90deg, #440154 0%, #3b528b 28%, #21918c 58%, #5dc863 80%, #c8df21 100%)',
    stops: ['#440154', '#3b528b', '#21918c', '#5dc863', '#c8df21'],
    chartPositive: '#15803d',
    chartNegative: '#c2410c',
    chartTrack: '#dce3ed'
  },
  {
    id: 'contrast',
    label: 'Ocean',
    description: 'Higher-contrast navy-to-teal scale without blown-out highlights.',
    swatch: 'linear-gradient(90deg, #203a5c 0%, #235789 24%, #1f7a8c 52%, #1fa187 78%, #8bd3c7 100%)',
    stops: ['#203a5c', '#235789', '#1f7a8c', '#1fa187', '#8bd3c7'],
    chartPositive: '#1f7a8c',
    chartNegative: '#235789',
    chartTrack: '#d8e5ea'
  }
];

const paletteStops = paletteModes.reduce((acc, mode) => {
  acc[mode.id] = mode.stops.map(hexToRgb);
  return acc;
}, {} as Record<PaletteModeId, rgb[]>);

function hexToRgb(hex: string): rgb {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

function mix(base: rgb, overlay: rgb, amount: number): rgb {
  const weight = Math.min(Math.max(amount, 0), 1);
  return {
    r: Math.round(base.r + (overlay.r - base.r) * weight),
    g: Math.round(base.g + (overlay.g - base.g) * weight),
    b: Math.round(base.b + (overlay.b - base.b) * weight)
  };
}

function interpolate(stops: rgb[], amount: number): rgb {
  if (stops.length === 1) {
    return stops[0];
  }

  const normalized = Math.min(Math.max(amount, 0), 1);
  const index = Math.min(stops.length - 2, Math.floor(normalized * (stops.length - 1)));
  const start = stops[index];
  const end = stops[index + 1];
  const localAmount = normalized * (stops.length - 1) - index;

  return mix(start, end, localAmount);
}

export function getPaletteMode(id: PaletteModeId = defaultPaletteMode): paletteDefinition {
  return paletteModes.find((mode) => mode.id === id) || paletteModes[0];
}

export function getChartColors(id: PaletteModeId = defaultPaletteMode) {
  const mode = getPaletteMode(id);
  return {
    positive: mode.chartPositive,
    negative: mode.chartNegative,
    track: mode.chartTrack
  };
}

type FillEmphasis = 'full' | 'soft' | 'muted';

function getFillColor(score: number, paletteMode: PaletteModeId, emphasis: FillEmphasis): string {
  const stops = paletteStops[paletteMode] || paletteStops[defaultPaletteMode];
  const base = interpolate(stops, score / 100);
  const fill =
    emphasis === 'full'
      ? base
      : emphasis === 'soft'
        ? mix(base, neutralFill, 0.72)
        : mix(base, neutralFill, 0.84);
  const alpha = emphasis === 'full' ? 1 : emphasis === 'soft' ? 0.55 : 0.22;
  return `rgba(${fill.r}, ${fill.g}, ${fill.b}, ${alpha})`;
}

export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + width - radius.tr, y);
  ctx.bezierCurveTo(x + width - radius.tr * c, y, x + width, y + radius.tr * c, x + width, y + radius.tr);
  ctx.lineTo(x + width, y + height - radius.br);
  ctx.bezierCurveTo(x + width, y + height - radius.br * c, x + width - radius.br * c, y + height, x + width - radius.br, y + height);
  ctx.lineTo(x + radius.bl, y + height);
  ctx.bezierCurveTo(x + radius.bl * c, y + height, x, y + height - radius.bl * c, x, y + height - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.bezierCurveTo(x, y + radius.tl * c, x + radius.tl * c, y, x + radius.tl, y);
  ctx.closePath();
  ctx.fill();
}

function getStateCode(cell): string {
  return cell && cell.s ? cell.s : '';
}

function sameState(a, b): boolean {
  const state = getStateCode(a);
  return Boolean(state) && state === getStateCode(b);
}

export function drawCanvas(
  ctx: CanvasRenderingContext2D,
  data,
  transform: DOMMatrix2DInit,
  width: number,
  cell: number,
  search: string,
  paletteMode: PaletteModeId,
  showStateBorders: boolean,
  scoreRange: ScoreRange
) {
  const height = width * data.length / data[0].length;
  const rangeFilterActive = scoreRange.min > 0 || scoreRange.max < 100;
  data.forEach((t, i) => t.forEach((l, j) => {
    if (l.c) {
      const topOccupied = i > 0 && Boolean(data[i - 1][j] && data[i - 1][j].c);
      const bottomOccupied = i < data.length - 1 && Boolean(data[i + 1][j] && data[i + 1][j].c);
      const leftOccupied = j > 0 && Boolean(data[i][j - 1] && data[i][j - 1].c);
      const rightOccupied = j < data[i].length - 1 && Boolean(data[i][j + 1] && data[i][j + 1].c);
      const topSameState = i > 0 && sameState(l, data[i - 1][j]);
      const bottomSameState = i < data.length - 1 && sameState(l, data[i + 1][j]);
      const leftSameState = j > 0 && sameState(l, data[i][j - 1]);
      const rightSameState = j < data[i].length - 1 && sameState(l, data[i][j + 1]);

      const sharedGap = cell * 0.08;
      const borderGap = showStateBorders ? cell * 0.4 : sharedGap;
      const leftInset = showStateBorders && leftOccupied && leftSameState ? sharedGap / 2 : borderGap / 2;
      const rightInset = showStateBorders && rightOccupied && rightSameState ? sharedGap / 2 : borderGap / 2;
      const topInset = showStateBorders && topOccupied && topSameState ? sharedGap / 2 : borderGap / 2;
      const bottomInset = showStateBorders && bottomOccupied && bottomSameState ? sharedGap / 2 : borderGap / 2;
      const drawX = j * cell + leftInset;
      const drawY = i * cell + topInset;
      const drawWidth = cell - leftInset - rightInset;
      const drawHeight = cell - topInset - bottomInset;

      if (drawX * transform.a < -transform.e + width && (drawX + drawWidth) * transform.a > -transform.e && drawY * transform.a < -transform.f + height && (drawY + drawHeight) * transform.a > -transform.f) {
        const minSide = Math.min(drawWidth, drawHeight);
        const baseRadius = minSide / 6;
        const outerRadius = minSide / 2;
        const radius = { tl: baseRadius, tr: baseRadius, br: baseRadius, bl: baseRadius }

        if (!topOccupied && !leftOccupied)
          radius.tl = outerRadius;
        if (!topOccupied && !rightOccupied)
          radius.tr = outerRadius;
        if (!bottomOccupied && !rightOccupied)
          radius.br = outerRadius;
        if (!bottomOccupied && !leftOccupied)
          radius.bl = outerRadius;

        const score = Math.max(0, Math.min(getWeatherScore(l), 100));
        const matchesSearch =
          search.length === 0
            ? true
            : Boolean(
                l.z?.some((zip) => (`00000${zip}`).slice(-5).slice(0, search.length) === search)
              );
        const inSelectedRange = score >= scoreRange.min && score <= scoreRange.max;
        const isHighlighted = matchesSearch && inSelectedRange;
        const emphasis: FillEmphasis = isHighlighted
          ? 'full'
          : rangeFilterActive
            ? 'muted'
            : 'soft';

        ctx.fillStyle = getFillColor(score, paletteMode, emphasis);
        roundRect(ctx, drawX, drawY, drawWidth, drawHeight, radius);
        if (isHighlighted && rangeFilterActive) {
          ctx.lineWidth = Math.max(1, cell * 0.06);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.82)';
          ctx.stroke();
        }
      }
    }
  }));
}
